import React, { useState, useEffect, useCallback } from "react";
import EnhancedRichTextEditor from "./components/EnhancedRichTextEditor";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  TextField,
  Grid,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Palette as PaletteIcon,
  ColorLens as ColorIcon,
} from "@mui/icons-material";
import api from "./api";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "./utils/languages";
import ColorEmojiPicker from "./components/ColorEmojiPicker";
import { CATEGORY_EMOJI_CATEGORIES } from "./utils/emojiCategories";

export default function SloganManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [editDialog, setEditDialog] = useState({
    open: false,
    category: null,
    slogan: "",
    color: "",
    emoji: "",
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/slogan-management/categories");
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      showNotification("Failed to fetch categories", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleEditSlogan = (category) => {
    const currentSlogan = getSloganForLanguage(category, currentLanguage);
    setEditDialog({
      open: true,
      category,
      slogan: currentSlogan || "",
      color: category?.color || "#3B82F6",
      emoji: category?.emoji || "📦",
    });
  };

  const handleSaveSlogan = async () => {
    if (!editDialog.category || !editDialog.category.id) {
      showNotification("Category is required", "error");
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(
        `/slogan-management/category/${editDialog.category.id}/slogan`,
        {
          language: currentLanguage,
          slogan: editDialog.slogan,
          color: editDialog.color,
          emoji: editDialog.emoji,
          autoTranslate: true,
        }
      );

      if (response.data.success) {
        showNotification(response.data.message, "success");
        await fetchCategories();
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error("Error saving slogan:", error);
      showNotification("Failed to save slogan", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialog({
      open: false,
      category: null,
      slogan: "",
      color: "",
      emoji: "",
    });
  };

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const getSloganForLanguage = (category, language) => {
    if (!category) return null;
    
    // First check for the slogan in languageSpec
    if (
      category.languageSpec && 
      category.languageSpec[language] && 
      category.languageSpec[language].slogan
    ) {
      return category.languageSpec[language].slogan;
    }
    
    // For backward compatibility - check if there's a slogans object with language mapping
    if (category.slogans && category.slogans[language]) {
      return category.slogans[language];
    }
    
    // For legacy data - check if the slogan field is a JSON object
    if (typeof category.slogan === 'object' && category.slogan && category.slogan[language]) {
      return category.slogan[language];
    }
    
    // If language is english or the default language, return the simple slogan string as fallback
    if (language === 'english' || language === DEFAULT_LANGUAGE) {
      return typeof category.slogan === 'string' ? category.slogan : null;
    }
    
    return null;
  };

  const hasSlogan = (category, language) => {
    return !!getSloganForLanguage(category, language);
  };

  const getCategoryName = (category) => {
    if (!category) return '';
    if (category.languageSpec && category.languageSpec[currentLanguage] && category.languageSpec[currentLanguage].name) {
      return category.languageSpec[currentLanguage].name;
    }
    return category.name || ''; // fallback to default name
  };

  // Helper function to get contrast color (for display purposes)
  const getContrastColor = (hex) => {
    // Convert hex to RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Slogan Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage category slogans across different languages
      </Typography>

      {/* Language Selector */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select Language:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {AVAILABLE_LANGUAGES.map((lang) => (
            <Chip
              key={lang}
              label={lang.charAt(0).toUpperCase() + lang.slice(1)}
              color={currentLanguage === lang ? "primary" : "default"}
              onClick={() => setCurrentLanguage(lang)}
              variant={currentLanguage === lang ? "filled" : "outlined"}
            />
          ))}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Emoji</TableCell>
              <TableCell>Slogan ({currentLanguage})</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => {
              const currentSlogan = getSloganForLanguage(category, currentLanguage);
              const hasCurrentSlogan = hasSlogan(category, currentLanguage);

              return (
                <TableRow key={category.id}>
                  <TableCell>
                    <Typography variant="subtitle1">
                      {getCategoryName(category)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: category.color || '#3B82F6',
                          border: '1px solid #ccc',
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {category.color || '#3B82F6'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" sx={{ fontSize: '1.5rem' }}>
                      {category.emoji || '📦'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {hasCurrentSlogan ? (
                      <Box 
                        sx={{ 
                          fontStyle: "italic",
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '& p': { margin: 0 },
                          '& h1, & h2, & h3': { margin: 0, fontSize: '1rem' },
                          '& ul, & ol': { margin: 0, paddingLeft: '1rem' },
                          '& blockquote': { margin: 0, paddingLeft: '1rem', borderLeft: '2px solid #ccc' },
                          '& .emoji-inline, & img[data-emoji-name], & img[alt*="emoji"]': {
                            height: '1em',
                            width: 'auto',
                            display: 'inline',
                            verticalAlign: 'middle',
                            margin: '0 0.1em'
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: currentSlogan }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No slogan for {currentLanguage}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditSlogan(category)}
                      color="primary"
                    >
                      {hasCurrentSlogan ? <EditIcon /> : <AddIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Slogan Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {getCategoryName(editDialog.category) || 'Category'} - {currentLanguage} Slogan & Styling
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Update the slogan, color, and emoji for this category. You can modify any of these independently. The slogan will be auto-detected for language and translated to other languages in the background.
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <ColorEmojiPicker
                  color={editDialog.color}
                  emoji={editDialog.emoji}
                  onColorChange={(color) => setEditDialog({ ...editDialog, color })}
                  onEmojiChange={(emoji) => setEditDialog({ ...editDialog, emoji })}
                  emojiCategories={CATEGORY_EMOJI_CATEGORIES}
                  previewLabel={getCategoryName(editDialog.category) || 'Category'}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Enter a catchy slogan for this category:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem', fontStyle: 'italic' }}>
                  You can enter text in any language - the system will automatically detect the language and translate it to other languages in the background.
                </Typography>
                <EnhancedRichTextEditor
                  content={editDialog.slogan}
                  onChange={(content) =>
                    setEditDialog({ ...editDialog, slogan: content })
                  }
                  placeholder="Enter your slogan here..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleSaveSlogan}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}